import {
  Button,
  buttonVariants,
} from "@arcadeai/design-system/components/ui/atoms/button";
import { cn } from "@arcadeai/design-system/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  useCreateCheckout,
  useSuspenseGetSubscription,
} from "@/app/billing/api/hooks";
import { useScheduledChangeHelpers } from "@/app/billing/api/use-scheduled-change-helpers";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { PLAN_IDS, type Plan as PricingPlan } from "../plans";

const getDowngradeEmailHref = (organizationId: string | undefined) => {
  const subject = encodeURIComponent("Downgrade from Growth to Hobby");
  const body = encodeURIComponent(
    `Hi Arcade team,\n\nPlease downgrade (or cancel) our subscription from Growth to Hobby.\n\nOrganization ID: ${organizationId}\n\nThanks!`
  );
  return `mailto:sales@arcade.dev?subject=${subject}&body=${body}`;
};

type PricingCardCtaProps = {
  isCurrent?: boolean;
  isNext?: boolean;
  ctaHref?: string;
  cta: string;
  id: string;
  buttonVariant: PricingPlan["buttonVariant"];
  customButtonClass?: string;
};

export function PricingCardCta({
  isCurrent,
  isNext,
  ctaHref,
  cta,
  id,
  buttonVariant,
  customButtonClass,
}: PricingCardCtaProps) {
  const navigate = useNavigate({
    from: "/orgs/$organizationId/projects/$projectId/billing/upgrade",
  });
  const { organizationId } = useOrgProjectRouteContext();
  const { data: subscription } = useSuspenseGetSubscription(organizationId);
  const { hasScheduledChangeTo } = useScheduledChangeHelpers(organizationId);
  const createCheckoutMutation = useCreateCheckout(organizationId, {
    onError: (error) => {
      toast.error("Failed to create checkout session", {
        description: error.message || "Please try again later.",
      });
    },
  });

  // Check if this plan has a scheduled change
  const hasScheduledChangeToThisPlan = hasScheduledChangeTo(id);

  const handleUpgradeClick = () => {
    const originalPlanId = subscription?.plan?.external_plan_id || "enterprise";

    createCheckoutMutation.mutate(
      { plan_id: id },
      {
        onSuccess: (data) => {
          navigate({
            params: { planId: id },
            search: {
              customerSessionSecret: data.data.stripe_customer_session_secret,
              paymentIntentSecret: data.data.stripe_payment_intent_secret,
              setupIntentSecret: data.data.stripe_setup_intent_secret,
              ...(originalPlanId && { originalPlanId }),
            },
            to: "./checkout/$planId",
          });
        },
      }
    );
  };

  if (isCurrent) {
    return (
      <Button
        className={cn(
          "relative z-[2] w-full uppercase tracking-wider",
          buttonVariants({ variant: "default" }),
          "cursor-default",
          "disabled:border-border disabled:bg-muted disabled:text-muted-foreground disabled:opacity-70"
        )}
        disabled
      >
        Current Plan
      </Button>
    );
  }

  if (hasScheduledChangeToThisPlan && !isCurrent) {
    return (
      <Button
        className={cn(
          "relative z-[2] w-full uppercase tracking-wider",
          buttonVariants({ variant: "outline" }),
          "cursor-default",
          "disabled:border-muted-foreground/30 disabled:bg-muted disabled:text-muted-foreground disabled:opacity-70"
        )}
        disabled
      >
        Scheduled
      </Button>
    );
  }

  const buttonText = isNext ? "Upgrade" : cta;

  const isGrowthPlan = subscription?.plan?.external_plan_id === PLAN_IDS.GROWTH;

  const shouldEmailForDowngrade = isGrowthPlan && id === PLAN_IDS.FREE;
  const linkHref = shouldEmailForDowngrade
    ? getDowngradeEmailHref(organizationId)
    : ctaHref;
  const linkText = shouldEmailForDowngrade ? "Email us" : buttonText;

  if (linkHref) {
    return (
      <a
        className={cn("relative z-[2] block w-full")}
        href={linkHref}
        rel="noopener noreferrer"
        target="_blank"
      >
        <Button
          className={cn(
            "w-full uppercase tracking-wider",
            buttonVariants({ variant: buttonVariant }),
            customButtonClass,
            buttonVariant === "outline" && "font-medium text-foreground"
          )}
        >
          {linkText}
        </Button>
      </a>
    );
  }

  return (
    <Button
      className={cn(
        "relative z-[2] w-full uppercase tracking-wider",
        buttonVariants({ variant: buttonVariant }),
        customButtonClass,
        buttonVariant === "outline" && "font-medium text-foreground"
      )}
      disabled={createCheckoutMutation.isPending}
      onClick={handleUpgradeClick}
    >
      {createCheckoutMutation.isPending ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          One moment...
        </>
      ) : (
        buttonText
      )}
    </Button>
  );
}
